import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { HuddleChatInput } from '@avslack/shared';
import { useHuddle } from '../huddle/HuddleProvider';
import { huddleChatKey, useHuddleChat, useSendHuddleChat } from '../data/huddle-chat';
import { useUsers } from '../data/users';
import { useGroups } from '../data/workspace';
import { Attachments } from './Attachments';
import { Composer } from './Composer';
import { Icon } from './Icon';
import { RichText } from './RichText';

/**
 * A hivas chatje.
 *
 * **Nem kulon, hivas-elettartamu chat.** Amit ide irunk, az a CSATORNABA megy
 * (DM-olelesnel a DM-be), megjelolve, hogy hivasbol jott. Ket okbol:
 *
 *  - **A hivas vege nem torol.** Egy cim, egy hatarido, egy dontes ott marad,
 *    ahol keresni fogjak. Egy hivassal egyutt eltuno chat pont a fontos
 *    dolgokat veszitene el.
 *  - **A kint levok is latjak** - azonnal, nem a vegen egyben. Aki nem tudott
 *    bemenni, igy is kovetheti, ha valami fontos hangzik el.
 *
 * A panel viszont CSAK a hivas uzeneteit mutatja, a csatorna elozmenyet nem:
 * a behivott vendeg igy sem lat bele a csatornaba.
 */
export function HuddleChat({ meId, onClose }: { meId: string; onClose: () => void }) {
  const { huddle, chatSignal, notifyChat } = useHuddle();
  const queryClient = useQueryClient();
  const { users, usersById, nameOf } = useUsers();
  const { data: groups } = useGroups();

  const huddleId = huddle?.id ?? null;
  const { data: messages, isLoading } = useHuddleChat(huddleId);
  const send = useSendHuddleChat(huddleId);

  /**
   * Az utoljara ELBUKOTT kuldes - az "Újra" gomb ezt kuldi el ismet.
   *
   * A szerkeszto kuldeskor kiurul (a feltoltesekkel egyutt), tehat a tartalmat
   * nem tudjuk visszatenni bele. Egy elnyelt uzenet viszont rosszabb, mint egy
   * latszo hiba: igy egy kattintassal ujra elmegy, formazasostul, kepestul.
   */
  const [failed, setFailed] = useState<Omit<HuddleChatInput, 'clientMsgId'> | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  /*
    A media-csatornan erkezo jelzesre AZONNAL frissitunk.

    A lekerdezes onmagaban 5 masodpercenkent fut - egy elo beszelgetesben ez
    erezhetoen keses lenne. A jelzes hozza a gyorsasagot, a lekerdezes a
    megbizhatosagot: ha egy jelzes elveszik, a kovetkezo kor behozza.
  */
  useEffect(() => {
    if (chatSignal === 0 || !huddleId) return;
    void queryClient.invalidateQueries({ queryKey: huddleChatKey(huddleId) });
  }, [chatSignal, huddleId, queryClient]);

  // Uj uzenetnel a lista aljara gorgetunk - a hivas kozben senki nem akar
  // gorgetni azert, hogy lassa, mit irtak most.
  useEffect(() => {
    const box = listRef.current;
    if (box) box.scrollTop = box.scrollHeight;
  }, [messages]);

  const submit = (input: Omit<HuddleChatInput, 'clientMsgId'>) => {
    if (!huddleId) return;

    setFailed(null);
    send.mutate(input, {
      onSuccess: () => notifyChat(),
      onError: () => setFailed(input),
    });
  };

  return (
    /*
      SZELESEBB panel, mint amig csak szovegdoboz volt: a teljes szerkeszto
      formazo-sorral, csatolmany-sorral es GIF-elonezettel 320 keppontban mar
      szorongott.
    */
    <aside className="flex shrink-0 flex-col max-sm:absolute max-sm:inset-0 max-sm:z-30 max-sm:bg-surface-raised border-l border-border-soft bg-surface-raised sm:w-96">
      <header className="flex items-center gap-2 border-b border-border-soft px-3 py-2">
        <Icon name="csatorna" size="sm" />
        <p className="min-w-0 flex-1 truncate text-xs font-semibold">Ölelés-chat</p>
        <button
          type="button"
          onClick={onClose}
          aria-label="Chat bezárása"
          className="rounded-control px-1.5 py-1 text-ink-faint transition-colors hover:bg-surface-hover hover:text-ink"
        >
          <Icon name="bezaras" size="sm" />
        </button>
      </header>

      <div
        ref={listRef}
        role="log"
        aria-label="Ölelés-chat üzenetei"
        className="min-h-0 flex-1 space-y-2.5 overflow-y-auto px-3 py-2.5"
      >
        {isLoading && <p className="text-2xs text-ink-faint">Betöltés…</p>}

        {!isLoading && (messages?.length ?? 0) === 0 && (
          <p className="text-2xs text-ink-faint">
            Még nincs üzenet. Amit ide írsz, a csatornába is bekerül — így a kint lévők is látják.
          </p>
        )}

        {messages?.map((message) => (
          <div key={message.id}>
            <p className="text-2xs text-ink-faint">
              <span className="font-medium text-ink-muted">
                {message.userId === meId
                  ? 'Te'
                  : typeof message.meta?.username === 'string'
                    ? message.meta.username
                    : nameOf(message.userId ?? '', 'Valaki')}
              </span>{' '}
              {new Date(message.createdAt).toLocaleTimeString('hu-HU', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>
            {/*
              UGYANAZ a megjelenites, mint a csatornaban: formazas, emlites,
              GIF, egyedi emoji - es alatta a kepek, fajlok.
            */}
            {(message.body || message.text) && (
              <div className="avslack-prose break-words text-sm text-ink">
                {message.body ? (
                  <RichText doc={message.body} meId={meId} usersById={usersById} />
                ) : (
                  <p className="whitespace-pre-wrap">{message.text}</p>
                )}
              </div>
            )}
            <Attachments files={message.files} />
          </div>
        ))}

        {failed && (
          <p role="alert" className="flex items-center gap-2 text-2xs text-[var(--danger)]">
            Az üzenet nem ment el.
            <button
              type="button"
              onClick={() => submit(failed)}
              disabled={send.isPending}
              className="rounded px-1.5 py-0.5 font-semibold underline underline-offset-2 hover:bg-surface-hover"
            >
              Újra
            </button>
          </p>
        )}
      </div>

      {/*
        A CSATORNA szerkesztoje, keskeny valtozatban: formazas, emoji, GIF,
        fajl es kep (Ctrl+V-vel es rahuzassal is), emlites. Idozitett kuldes
        nincs - a hivas chatje az eppen zajlo beszelgeteshez tartozik.
      */}
      <div className="border-t border-border-soft">
        <Composer
          compact
          placeholder="Üzenet a hívásba — a csatornába is bekerül…"
          users={users}
          groups={groups ?? []}
          meId={meId}
          onSend={(value, fileIds, isImportant) =>
            submit({
              text: value.text,
              body: value.body,
              ...(fileIds.length > 0 ? { fileIds } : {}),
              ...(isImportant ? { isImportant } : {}),
            })
          }
        />
      </div>
    </aside>
  );
}
